import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiTrendingDown, FiEdit3, FiCalendar, FiMessageSquare, FiTrash2, FiAlertTriangle, FiInbox } = FiIcons

const TradesList = ({ trades, loading, onEditTrade, onDeleteTrade }) => {
  const [deletingId, setDeletingId] = useState(null);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-8">
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Syncing with database...</p>
        </div>
      </div>
    )
  }

  if (trades.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiInbox} className="h-8 w-8 text-gray-300 dark:text-slate-500" />
        </div>
        <p className="text-lg font-medium text-gray-900 dark:text-white">No trades recorded</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Your trading journal is currently empty.</p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trade History</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">{trades.length} trades recorded</span>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-slate-700">
        <AnimatePresence>
          {trades.map((trade, index) => (
            <motion.div 
              key={trade.id} 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, x: -20 }}
              transition={{ delay: index * 0.03 }} 
              className="p-6 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors group"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2.5 rounded-xl ${(trade.pnl || 0) >= 0 ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
                      <SafeIcon icon={(trade.pnl || 0) >= 0 ? FiTrendingUp : FiTrendingDown} className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {trade.symbol}
                        </h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${trade.side === 'buy' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'}`}>
                          {trade.side}
                        </span>
                        <span className={`px-2 py-1 rounded-lg text-sm font-bold ${(trade.pnl || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {(trade.pnl || 0) >= 0 ? '+' : '-'}Rp {Math.abs(trade.pnl || 0).toLocaleString('id-ID')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiCalendar} className="h-4 w-4" />
                          <span>
                            {trade.entry_time ? format(new Date(trade.entry_time), 'MMM dd, HH:mm') : 'No date'}
                          </span>
                        </div>
                        {trade.setup_type && (
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            {trade.setup_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => onEditTrade(trade)} 
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                    >
                      <SafeIcon icon={FiEdit3} className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => setDeletingId(trade.id)} 
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                    >
                      <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {trade.notes && (
                  <div className="bg-gray-50 dark:bg-slate-700/50 border-l-4 border-gray-200 dark:border-slate-600 p-3 rounded-r-lg ml-14">
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                      {trade.notes}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {deletingId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Trade?</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">This action cannot be undone. Are you sure?</p>
            <div className="flex space-x-3">
              <button onClick={() => setDeletingId(null)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium">Cancel</button>
              <button onClick={() => { onDeleteTrade(deletingId); setDeletingId(null); }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-medium">Delete</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default TradesList